<script setup>
import { defineProps, defineEmits, onMounted, onUpdated, ref, computed } from "vue";
import { KNOLIA } from "../knolia";
import { Gems } from "@/gems";
import { useTreeStore } from '@/store/modules/trees.js';
import DepthElement from './DepthElement.vue';
import { MESSAGES } from "../messages";
import TreeType from "../enums/TreeType";

const props = defineProps({
  column: { type: Object, required: true },
  isKnowledge: { type: Boolean },
  treeType: { type: Number, required: true },
})

const emit = defineEmits(["filter-children"])
const showOptions = ref(false)
const store = useTreeStore()

const designation = computed({
  get() {
    return props.column.designation
  },
  set(newValue) {
    Object.assign(props.column, {designation: newValue})
  }
})

const groupedItems = computed(() => {
  const groups = []
  let currentGroup = null

  for (const item of props.column.elements) {
    if (!currentGroup || currentGroup.parent_id !== item.parent_id) {
      currentGroup = {
        id: item.parent_id,
        parent_designation: item.parent_designation,
        parent_id: item.parent_id,
        depth: item.depth,
        showSelf: item.showSelf,
        children: []
      }

      groups.push(currentGroup)
    }
    currentGroup.children.push(item)
  }

  return groups
})

const depthLimit = computed({
  get() {
    return props.treeType == TreeType.SKILLS ? props.column.totalDepth - 2 : props.column.totalDepth - 1
  }
})

function ChangeColumnName(column) {
  store.UpdateDepthName({
    name: column.designation,
    element: column,
    update: "name",
    treeType: props.treeType,
  })
  showOptions.value = false
}

function DeleteColumn(column) {
  const treeTypeName = props.treeType === TreeType.SKILLS ? 'skills' : 'tasks'

  Gems.Telegram(MESSAGES.QUESTIONS.REMOVE_COLUMN_TREE, "tele-info", "", "tele-cancel").then(
    () => {
      KNOLIA.CallBackEnd("post", "delete-column", {
        column: column,
        tree: treeTypeName,
      }).then((res) => {
        if (res.error) {
          Gems.Telegram(MESSAGES.ERROR.DEFAULT, "tele-not-allowed")
          return
        } 
        Gems.Telegram(MESSAGES.SUCCESS.REMOVED, "tele-success")

        if (props.treeType === TreeType.SKILLS) {
          store.UpdateSkillsTree()
        } else {
          store.UpdateTasksTree()
        }

      });
    },
    () => {}
  );
}

function ShowOptions() {
  if(props.column.depth > depthLimit.value - 1)
    return
  
  showOptions.value = true
}

function ParentHover(group) {
  const parentElement = document.getElementById(`${group.parent_designation + group.parent_id}`)

  if(parentElement)
    parentElement.classList.toggle('highlight-element')
}

onMounted(() => {
  Object.assign(props.column, { is_knowledge: KNOLIA.ToBoolean(props.column.is_knowledge)})
  store.setColumnsTooltip(props.column)
});

onUpdated(() => {
  store.setColumnsTooltip(props.column)
  const element = document.getElementById(`elements-container${props.column.depth}`)

  if(element) {
    if(element.scrollHeight > element.clientHeight)
      element.classList.add('elements-with-scroll')
    else
      element.classList.remove('elements-with-scroll')
  }
});
</script>

<template>
  <div>
    <div class="depth-container">
      <div class="depth-header">
        <div class="div-column-icons">
          <i class="fa fa-plus-circle i-add-meta" :id="`new-column${column.id}`"
            @click="store.NewColumn({meta_id: column.meta_id, depth: column.depth, totalDepth: column.totalDepth, elements: column.elements, treeType: props.treeType})"
            v-show="column.depth <= depthLimit"></i>
        </div>

        <div class="depth" @click="store.showAllElements({depth: column.depth, meta_id: column.meta_id})"
          :id="`depth${column.depth}`">
          Nível {{ column.depth }}
        </div>
        
        <div class="div-column-icons justify-content-end">
          <i class="fa fa-trash cursor-pointer" :id="`remove-column${column.id}`" @click="DeleteColumn(column)" v-if="column.depth <= depthLimit - 1"></i>
        </div>
      </div>

      <div class="depth-header depth-second-header">
        <div></div>
        <input
          class="col-name"
          type="text"
          v-model="designation"
          placeholder="[Nome do nível]"
          :disabled="column.depth > depthLimit - 1"
          :readonly="!showOptions"
          :title="props.column.designation" 
          @dblclick="ShowOptions()"
        />
        <div class="div-menu-edit align-items-end" v-show="showOptions">
          <i class="fa fa-check" :id="`confirm-column-name-icon-${column.id}`" @click="ChangeColumnName(column)"></i>
          <i class="fa fa-times" :id="`cancel-column-name-icon-${column.id}`" @click="showOptions = false"></i>
        </div>
      </div>
    </div>

    <div class="elements-container scrollbar" :id="`elements-container${props.column.depth}`">

      <div v-for="(group, index) in groupedItems" :key="index" class="div-parent-group" 
          v-show="group.showSelf == true || props.column.depth == 1">
        <span class="parent-name line-clamp line-clamp-1" :id="`parent-name${group.id}`" @mouseenter="ParentHover(group)" @mouseleave="ParentHover(group)">{{ group.parent_designation }}</span>

        <DepthElement :element="element" :class="{show: element.showSelf == true || column.depth == 1}"
          v-for="(element, ind) in group.children" :key="element.id"
          :column-depth="column.totalDepth"
          :meta-id="column.meta_id"
          :depth="column"
          :index="ind"
          :tree-type="props.treeType"
          @filter-children="emit('filter-children', $event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
@import './assets/styles/trees-style.css';

.div-parent-group {
  border: 1px solid;
  border-radius: 4px;
  margin-bottom: 2px;
}
</style>
